import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import {
  EditorContent,
  useEditor,
  generateJSON,
  generateHTML,
} from "@tiptap/react";
import { Node, mergeAttributes, Extension, CommandProps } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import FontFamily from "@tiptap/extension-font-family";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import Paragraph from "@tiptap/extension-paragraph";
import Dropcursor from "@tiptap/extension-dropcursor";
import Subscript from "@tiptap/extension-subscript";
import { ReactNodeViewRenderer } from "@tiptap/react";
import ImageNodeView from "./UploadImageNodeView";

// Table Extensions
import { Table, TableCell } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";

import TipTapToolbar from "./UploadTipTapToolbar";
import { useParams } from "react-router-dom";
import { DocumentUploadReport } from "../../../projectManagementQueries";
import { TipTapTitleBar } from "./UploadTipTapTitleBar";
import { toast } from "react-toastify";
import { selectTable } from "./selectTable";
import { FontSize } from "../../../../../data/FontSize";
import { EditorState, Transaction, TextSelection } from "@tiptap/pm/state";
import { CellSelection } from "@tiptap/pm/tables";
import { LineHeight } from "@tiptap/extension-text-style";
import LsdbButtonExtension from "./LSDBButton";
import { UseuploadEditorTipTapManualSave } from "../../../projectManagementMutations";
import { useFetchContext } from "../../../../common/FetchContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  LinearProgress,
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { FindReplaceWidget } from "../TipTap/FindReplaceWidget";
import {
  extractTOCItems,
  TableOfContents,
  TOCItem,
} from "../TipTap/TableOfContents";
import {
  scrollToHeading,
  scrollToHeadingByTitle,
  normalizeTitle,
  findTocBlockRanges,
  isForcedPageStartText,
} from "../TipTap/headingUtils";
import {
  splitTopLevelNodes,
  measureHtmlHeight,
  isEffectivelyEmpty,
  packNodesIntoPages,
} from "../TipTap/reportPageGenerator";

// ─────────────────────────────────────────────────────────────
// 1. INTERFACES
// ─────────────────────────────────────────────────────────────

interface PageSetup {
  widthInches: number;
  heightInches: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
}

interface HeaderConfig {
  label: string;
  src?: string;
  separatorColor?: string;
}

interface FooterConfig {
  left: string;
  center: string;
  separatorColor?: string;
  showPageNumbers?: boolean;
}

interface ChromeConfig {
  header: HeaderConfig;
  footer: FooterConfig;
  pageSetup: PageSetup;
  differentFirstPage?: boolean;
}

interface PageChromeProps {
  chrome: ChromeConfig;
  contentHeightPx: number;
  pageNumber: number;
  totalPages: number;
  isFirstPage: boolean;
  customHeaderHtml?: string;
  customFooterHtml?: string;
  children?: ReactNode;
  activeEditor?: any;
}

interface SinglePageEditorProps {
  contentHtml: string;
  maxHeightPx: number;
  onChange: (html: string) => void;
  onFocus: (editorInstance: any) => void;
  onUserEdit: () => void;
  onOverflow: (overflowHtml: string) => void;
  onEditorDestroy?: (editorInstance: any) => void;
}

// ─────────────────────────────────────────────────────────────
// 2. CUSTOM EXTENSIONS
// ─────────────────────────────────────────────────────────────

const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      textAlign: {
        default: null,
      },
      headingStyle: {
        default: null,
      },
      listNumber: {
        default: null,
      },
      lineHeight: {
        default: null,
        parseHTML: (element) => {
          return element.style.lineHeight || null;
        },
        renderHTML: (attributes) => {
          if (!attributes.lineHeight) {
            return {};
          }
          return {
            style: `line-height: ${attributes.lineHeight}`,
          };
        },
      },
      indent: {
        default: 0,

        parseHTML: (element) =>
          parseInt(element.getAttribute("data-indent") || "0", 10),

        renderHTML: (attributes) => {
          if (!attributes.indent) {
            return {};
          }

          return {
            "data-indent": attributes.indent,
            style: `margin-left: ${attributes.indent}px`,
          };
        },
      },
    };
  },
});

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      caption: { default: "" },
      width: { default: "auto" },
      height: { default: "auto" },
      rotation: { default: 0 },
      alignment: {
        default: "left",
        parseHTML: (element) =>
          element.getAttribute("data-alignment") || "left",
        renderHTML: (attributes) => {
          if (!attributes.alignment) return {};
          return {
            "data-alignment": attributes.alignment,
          };
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      name: { default: null },
      width: { default: null },
      number: { default: null },
    };
  },
});

const CustomTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      height: { default: null },
    };
  },
});

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-background-color"),
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) {
            return {};
          }
          return {
            "data-background-color": attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`,
          };
        },
      },
    };
  },
});

const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) return {};
          return { style: `background-color: ${attributes.backgroundColor}` };
        },
      },
      align: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.align) return {};
          return { style: `text-align: ${attributes.align}` };
        },
      },
      textDirection: {
        default: "horizontal",
        parseHTML: (element) => {
          const value = element.getAttribute("data-text-direction");
          if (value === "True" || value === "true") return "True";
          return value || "horizontal";
        },
        renderHTML: (attributes) => {
          const direction = attributes.textDirection || "horizontal";
          const isVertical =
            direction === "True" ||
            direction === "true" ||
            direction === "vertical";

          if (isVertical) {
            return {
              "data-text-direction": "True",
            };
          }
          return {
            "data-text-direction": "horizontal",
          };
        },
      },
    };
  },
});

const CustomHeader = Node.create({
  name: "header",
  group: "block",
  content: "block+",
  parseHTML() {
    return [{ tag: "header" }, { tag: "h2" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["header", mergeAttributes(HTMLAttributes), 0];
  },
});

const CustomFooter = Node.create({
  name: "footer",
  group: "block",
  content: "block+",
  parseHTML() {
    return [{ tag: "footer" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["footer", mergeAttributes(HTMLAttributes), 0];
  },
});

const ChromeExtension = Node.create({
  name: "chrome",
  group: "block",
  addAttributes() {
    return { data: { default: null } };
  },
  parseHTML() {
    return [{ tag: "div[data-chrome]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-chrome": "" })];
  },
});

const EditorPropsExtension = Node.create({
  name: "editor_props",
  group: "block",
  addAttributes() {
    return { data: { default: null } };
  },
  parseHTML() {
    return [{ tag: "div[data-editor-props]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-editor-props": "" }),
    ];
  },
});

const PageNumberExtension = Node.create({
  name: "pageNumber",
  group: "inline",
  inline: true,
  parseHTML() {
    return [{ tag: "span.page-number-node" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { class: "page-number-node" }),
      "#",
    ];
  },
});

const TotalPagesExtension = Node.create({
  name: "totalPages",
  group: "inline",
  inline: true,
  parseHTML() {
    return [{ tag: "span.total-pages-node" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { class: "total-pages-node" }),
      "##",
    ];
  },
});

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    letterSpacing: {
      setLetterSpacing: (value: string) => ReturnType;
      unsetLetterSpacing: () => ReturnType;
    };
  }
}

const CustomTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      letterSpacing: {
        default: null,
        parseHTML: (element) => {
          return element.style.letterSpacing || null;
        },
        renderHTML: (attributes) => {
          if (!attributes.letterSpacing) {
            return {};
          }
          return {
            style: `letter-spacing: ${attributes.letterSpacing}`,
          };
        },
      },
    };
  },
  addCommands() {
    return {
      setLetterSpacing:
        (value: string) =>
        ({ chain }: CommandProps) => {
          return chain()
            .setMark("textStyle", {
              letterSpacing: value,
            })
            .run();
        },
      unsetLetterSpacing:
        () =>
        ({ chain }: CommandProps) => {
          return chain()
            .setMark("textStyle", {
              letterSpacing: null,
            })
            .run();
        },
    };
  },
});

const ActionLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-action": { default: null },
      "data-images": { default: null },
    };
  },
});

const EDITOR_EXTENSIONS = [
  StarterKit.configure({ paragraph: false }),
  CustomParagraph,
  ActionLink.configure({ openOnClick: false }),
  Dropcursor,
  Underline,
  Subscript,
  Highlight.configure({ multicolor: true }),
  CustomTable.configure({ resizable: true }),
  CustomTableRow,
  CustomTableCell,
  CustomTableHeader,
  CustomImage,
  LsdbButtonExtension,
  CustomHeader,
  CustomFooter,
  ChromeExtension,
  EditorPropsExtension,
  PageNumberExtension,
  TotalPagesExtension,
  CustomTextStyle,
  FontFamily,
  FontSize,
  Color,
  LineHeight,
  TextAlign.configure({ types: ["heading", "paragraph", "header"] }),
];

// ─────────────────────────────────────────────────────────────
// 3. PAGE CHROME COMPONENT
// ─────────────────────────────────────────────────────────────

export function PageChrome({
  chrome,
  pageNumber,
  totalPages,
  isFirstPage,
  children,
  contentHeightPx,
  customHeaderHtml,
  customFooterHtml,
  activeEditor,
}: PageChromeProps): JSX.Element {
  const { header, footer, pageSetup } = chrome;

  const pageWidthPx = pageSetup.widthInches * 96;
  const pageHeightPx = pageSetup.heightInches * 96;
  const marginLeftPx = pageSetup.marginLeft * 96;
  const marginRightPx = pageSetup.marginRight * 96;

  const getImageSrc = (src?: string) => {
    if (!src) return "";
    if (src.startsWith("http") || src.startsWith("data:")) return src;
    if (src.startsWith("PHN2Z")) return `data:image/svg+xml;base64,${src}`;
    if (src.startsWith("iVBOR")) return `data:image/png;base64,${src}`;
    if (src.startsWith("/9j/")) return `data:image/jpeg;base64,${src}`;
    return src;
  };

  const showChrome = !isFirstPage;

  return (
    <div
      className={`docgen-page ${isFirstPage ? "cover-page" : ""}`}
      style={{
        width: pageWidthPx,
        minHeight: pageHeightPx,
        background: "#fff",
        border: "1px solid #e4e2dc",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        boxSizing: "border-box",
        marginBottom: 32,
        overflow: "hidden",
      }}
    >
      {showChrome && (
        <div
          className="docgen-page-header"
          style={{
            padding: `8px ${marginRightPx}px 16px ${marginLeftPx}px`,
            display: customHeaderHtml ? "block" : "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            borderBottom: `2px solid ${header?.separatorColor || "#095ae8"}`,
            flexShrink: 0,
            background: "#fff",
            zIndex: 10,
          }}
        >
          {customHeaderHtml ? (
            <div
              className="ProseMirror"
              dangerouslySetInnerHTML={{ __html: customHeaderHtml }}
              style={{
                width: "100%",
                minHeight: "auto",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            />
          ) : (
            <>
              <span
                style={{
                  fontFamily: "Arial",
                  fontSize: 10,
                  fontStyle: "italic",
                  color: "#888",
                }}
              >
                {header?.label}
              </span>
              {header?.src && (
                <img
                  src={getImageSrc(header?.src)}
                  alt="Logo"
                  style={{ width: 150, height: 40, objectFit: "contain" }}
                />
              )}
            </>
          )}
        </div>
      )}

      <div
        className="docgen-page-content"
        style={{
          maxHeight: isFirstPage ? pageHeightPx : contentHeightPx,
          minHeight: isFirstPage ? pageHeightPx : contentHeightPx,
          padding: isFirstPage ? "20px 40px" : "40px 80px 20px 80px",
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          flex: isFirstPage ? "0 0 100%" : 1,
          overflow: isFirstPage ? "hidden" : "visible",
        }}
      >
        {children}
      </div>

      {showChrome && (
        <div
          className="docgen-page-footer"
          style={{
            marginTop: "auto",
            padding: `12px ${marginRightPx}px 16px ${marginLeftPx}px`,
            display: customFooterHtml ? "block" : "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: `2px solid ${footer?.separatorColor || "#095ae8"}`,
            flexShrink: 0,
            fontFamily: "Arial",
            fontSize: 9,
            color: "#888",
            background: "#fff",
            zIndex: 10,
          }}
        >
          {customFooterHtml ? (
            <div
              className="ProseMirror"
              dangerouslySetInnerHTML={{
                __html: customFooterHtml
                  .replace(
                    /(<span[^>]*class="page-number-node"[^>]*>).*?(<\/span>)/g,
                    `$1${pageNumber}$2`,
                  )
                  .replace(
                    /(<span[^>]*class="total-pages-node"[^>]*>).*?(<\/span>)/g,
                    `$1${totalPages}$2`,
                  ),
              }}
              style={{ width: "100%", minHeight: "auto" }}
            />
          ) : (
            <>
              <span>{footer?.left}</span>
              <span>{footer?.center}</span>
              {footer?.showPageNumbers && (
                <span>
                  Page {pageNumber} | {totalPages}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 4. SINGLE PAGE EDITOR COMPONENT
// ─────────────────────────────────────────────────────────────

function splitTableRowsForOverflow(
  tableNode: any,
  tableEl: HTMLElement,
  containerTop: number,
  targetMaxHeight: number,
) {
  const rowEls = Array.from(tableEl.querySelectorAll("tr")) as HTMLElement[];
  const rows: any[] = [];
  tableNode.forEach((rowNode: any) => rows.push(rowNode.toJSON()));

  if (rowEls.length !== rows.length || rows.length === 0) return null;

  const isHeaderRow = (r: any) =>
    (r.content || []).length > 0 &&
    (r.content || []).every((c: any) => c.type === "tableHeader");
  const headerRows = rows.filter(isHeaderRow);

  let rowSplitIdx = rows.length;
  for (let i = 0; i < rowEls.length; i++) {
    const rowBottom = rowEls[i].getBoundingClientRect().bottom - containerTop;
    if (rowBottom > targetMaxHeight) {
      rowSplitIdx = i;
      break;
    }
  }

  if (rowSplitIdx >= rows.length) return null;
  if (rowSplitIdx <= headerRows.length) return null;

  const keepRows = rows.slice(0, rowSplitIdx);
  const remainderRows = rows.slice(rowSplitIdx).filter((r) => !isHeaderRow(r));
  if (remainderRows.length === 0) return null;

  const moveRows = [...headerRows, ...remainderRows];
  return { keepRows, moveRows };
}

function SinglePageEditor({
  contentHtml,
  maxHeightPx,
  onChange,
  onFocus,
  onUserEdit,
  onOverflow,
  onEditorDestroy,
}: SinglePageEditorProps) {
  const checkOverflowRef = useRef<() => void>(() => {});

  const onEditorDestroyRef = useRef(onEditorDestroy);
  useEffect(() => {
    onEditorDestroyRef.current = onEditorDestroy;
  }, [onEditorDestroy]);

  const editor = useEditor({
    extensions: EDITOR_EXTENSIONS,
    content: contentHtml,
    editorProps: {
      handleClickOn(view, pos, node, nodePos, event) {
        if (
          event.altKey &&
          (node.type.name === "table" ||
            node.type.name === "tableCell" ||
            node.type.name === "tableHeader")
        ) {
          selectTable(view.state, view.dispatch);
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          (view as any)._lastMouseDownAt = {
            x: (event as MouseEvent).clientX,
            y: (event as MouseEvent).clientY,
          };
          return false;
        },
      },
      handleClick(view, pos, event) {
        if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
          return false;
        }

        const start = (view as any)._lastMouseDownAt;
        const moved =
          !start ||
          Math.abs(start.x - event.clientX) > 4 ||
          Math.abs(start.y - event.clientY) > 4;

        if (!moved && view.state.selection instanceof CellSelection) {
          const $pos = view.state.doc.resolve(pos);
          const tr = view.state.tr.setSelection(TextSelection.near($pos));
          view.dispatch(tr);
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor, transaction }) => {
      onChange(editor.getHTML());
      if (transaction.docChanged && !editor.isCapturingTransaction)
        onUserEdit();
      requestAnimationFrame(() =>
        setTimeout(() => checkOverflowRef.current(), 50),
      );
    },
    onFocus: ({ editor }) => onFocus(editor),
    onDestroy: () => {
      onEditorDestroyRef.current?.(editor);
    },
  });

  const isSplittingRef = useRef(false);

  const checkOverflowAndSplit = useCallback(() => {
    if (!editor || editor.isDestroyed) return;
    if (isSplittingRef.current) return;
    const dom = editor.view.dom as HTMLElement;
    const doc = editor.state.doc;
    const OVERFLOW_TOLERANCE = 15;
    const targetMaxHeight = maxHeightPx + OVERFLOW_TOLERANCE;

    // A document's own "Table of Contents" block (title + entries) must
    // always end its own page. If this page contains a complete TOC block
    // followed by any other content, force a break right after the block
    // — even when the page still has room left — so nothing (e.g. a
    // Summary section) ever ends up sharing the TOC's page. Separately,
    // fixed section headings (e.g. "Report Details", "Table of Contents")
    // must always *start* a fresh page too, regardless of how much room
    // is left on the current one.
    let forcedSplitIndex: number | null = null;
    if (doc.childCount > 1) {
      const childTexts: string[] = [];
      for (let k = 0; k < doc.childCount; k++) {
        childTexts.push(doc.child(k).textContent || "");
      }
      const tocRangeWithTrailingContent = findTocBlockRanges(childTexts).find(
        (r) => r.end < doc.childCount - 1,
      );
      if (tocRangeWithTrailingContent) {
        forcedSplitIndex = tocRangeWithTrailingContent.end + 1;
      }

      for (let k = 1; k < childTexts.length; k++) {
        if (isForcedPageStartText(childTexts[k])) {
          forcedSplitIndex =
            forcedSplitIndex === null ? k : Math.min(forcedSplitIndex, k);
          break;
        }
      }
    }

    if (dom.scrollHeight <= targetMaxHeight && forcedSplitIndex === null) {
      return;
    }

    const children = Array.from(dom.children) as HTMLElement[];
    if (children.length === 0) return;

    const containerTop = dom.getBoundingClientRect().top;

    const releaseLockAfterSettle = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isSplittingRef.current = false;
        });
      });
    };

    if (doc.childCount === 1 && doc.child(0).type.name === "table") {
      const split = splitTableRowsForOverflow(
        doc.child(0),
        children[0],
        containerTop,
        targetMaxHeight,
      );
      if (split) {
        isSplittingRef.current = true;
        const { keepRows, moveRows } = split;
        const tableEnd = doc.child(0).nodeSize;
        editor
          .chain()
          .insertContentAt(
            { from: 0, to: tableEnd },
            { type: "table", content: keepRows },
          )
          .run();
        const overflowHtml = generateHTML(
          { type: "doc", content: [{ type: "table", content: moveRows }] },
          EDITOR_EXTENSIONS,
        );
        onOverflow(overflowHtml);
        releaseLockAfterSettle();
      }
      return;
    }

    if (doc.childCount <= 1) return;

    let splitIndex = children.length;
    for (let i = 0; i < children.length; i++) {
      const childBottom =
        children[i].getBoundingClientRect().bottom - containerTop;
      if (childBottom > targetMaxHeight) {
        splitIndex = i;
        break;
      }
    }

    // Whichever constraint requires the earlier cut wins: natural overflow
    // or the forced TOC/trailing-content boundary computed above.
    if (forcedSplitIndex !== null && forcedSplitIndex < splitIndex) {
      splitIndex = forcedSplitIndex;
    }

    if (splitIndex > 1 && splitIndex < doc.childCount) {
      const nodeToMove = doc.child(splitIndex);
      const previousNode = doc.child(splitIndex - 1);
      if (
        nodeToMove.type.name === "table" &&
        previousNode.type.name === "lsdbButton"
      ) {
        splitIndex -= 1;
      }
    }

    // Keep a document's own "Table of Contents" block (title + entries)
    // intact — never let it straddle a page boundary. If the computed
    // split point falls inside such a block, push the whole block to the
    // next page instead, the same way a table is moved whole rather than
    // split by row. (Skipped when the block starts at the very first
    // child, since moving it would empty the page entirely with nothing
    // gained — that pathological case is left to the normal split logic.)
    if (splitIndex > 0 && splitIndex < doc.childCount) {
      const childTexts: string[] = [];
      for (let k = 0; k < doc.childCount; k++) {
        childTexts.push(doc.child(k).textContent || "");
      }
      const tocRange = findTocBlockRanges(childTexts).find(
        (r) => splitIndex > r.start && splitIndex <= r.end,
      );
      if (tocRange && tocRange.start > 0) {
        splitIndex = tocRange.start;
      }
    }

    if (splitIndex === 0) splitIndex = 1;
    if (splitIndex >= doc.childCount) return;

    let fromPos = 0;
    for (let i = 0; i < splitIndex; i++) {
      fromPos += doc.child(i).nodeSize;
    }

    const toPos = doc.content.size;
    if (fromPos >= toPos) return;

    const overflowFragment = doc.slice(fromPos, toPos).content.toJSON() ?? [];
    const overflowHtml = generateHTML(
      { type: "doc", content: overflowFragment },
      EDITOR_EXTENSIONS,
    );

    isSplittingRef.current = true;
    editor.chain().deleteRange({ from: fromPos, to: toPos }).run();
    onOverflow(overflowHtml);
    releaseLockAfterSettle();
  }, [editor, maxHeightPx, onOverflow]);

  useEffect(() => {
    checkOverflowRef.current = checkOverflowAndSplit;
  }, [checkOverflowAndSplit]);

  const onFocusRef = useRef(onFocus);
  useEffect(() => {
    onFocusRef.current = onFocus;
  }, [onFocus]);
  useEffect(() => {
    if (editor) onFocusRef.current(editor);
  }, [editor]);

  useEffect(() => {
    if (editor && !editor.isDestroyed && contentHtml !== editor.getHTML()) {
      queueMicrotask(() => {
        if (!editor.isDestroyed) {
          editor.commands.setContent(contentHtml, { emitUpdate: false });
          requestAnimationFrame(() => checkOverflowRef.current());
        }
      });
    }
  }, [contentHtml, editor]);

  useEffect(() => {
    if (!editor) return;
    const observer = new ResizeObserver(() =>
      requestAnimationFrame(() => checkOverflowRef.current()),
    );
    observer.observe(editor.view.dom as HTMLElement);
    return () => observer.disconnect();
  }, [editor]);

  if (!editor) return null;
  return (
    <div
      style={{ minHeight: "100%", height: "100%", cursor: "text" }}
      onClick={(e) => {
        if (e.target === e.currentTarget && editor) {
          editor.commands.focus("end");
        }
      }}
    >
      <EditorContent editor={editor} style={{ height: "100%" }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 5. UTILS & PAGINATION ENGINE
// ─────────────────────────────────────────────────────────────
// splitTopLevelNodes / measureHtmlHeight / isEffectivelyEmpty / Figure
// packNodesIntoPages are all imported from TipTap's shared, dependency-free
// reportPageGenerator.ts (see imports above) so both editors paginate
// identically — including keeping a table/<lsdb-button> cluster or a
// document's own Table of Contents block intact and on one page.

const sanitizeTipTapJSON = (node: any): any => {
  if (Array.isArray(node)) {
    return node.map(sanitizeTipTapJSON).filter(Boolean);
  }

  if (node !== null && typeof node === "object") {
    if (node.type === "text" && (!node.text || node.text === "")) {
      return null;
    }
    const newNode = { ...node };
    return newNode;
  }

  return node;
};

// ─────────────────────────────────────────────────────────────
// 6. MAIN COMPONENT (UploadTipTapEditor)
// ─────────────────────────────────────────────────────────────

export const UploadTipTapEditor = () => {
  const { template_id } = useParams() as { template_id: string };
  const [zoom, setZoom] = useState(100);
  const [darkMode, setDarkMode] = useState(false);

  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");

  // NAVIGATION PANE: lets users jump between document sections (headings).
  const [showNavPane, setShowNavPane] = useState(false);
  const [navHeadings, setNavHeadings] = useState<
    {
      pageIndex: number;
      indexWithinPage: number;
      level: number;
      text: string;
    }[]
  >([]);
  const [showDeletePageDialog, setShowDeletePageDialog] = useState(false);
  const [deletePageIndex, setDeletePageIndex] = useState<number | null>(null);
  const [deletePageNumber, setDeletePageNumber] = useState<number | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  const handleNavHeadingClick = useCallback(
    (heading: { pageIndex: number; indexWithinPage: number }) => {
      const pageContentEls = document.querySelectorAll(
        ".docgen-page-content .ProseMirror",
      );
      const targetPage = pageContentEls[heading.pageIndex] as
        | HTMLElement
        | undefined;
      if (!targetPage) return;
      const headingEls = targetPage.querySelectorAll("h1, h2, h3");
      const targetHeading = headingEls[heading.indexWithinPage] as
        | HTMLElement
        | undefined;
      targetHeading?.scrollIntoView({ behavior: "smooth", block: "start" });
      setShowNavPane(false);
    },
    [],
  );

  const [matches, setMatches] = useState<{ from: number; to: number }[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  const reportNumber = "Report-000";
  const reportVersion = "1.0";
  const version_history: any[] | undefined = [];
  const reportData = { status: "Draft" };
  const ProjectId = "R-000";
  const workOrder = "0";
  const reportImages: any[] = [];
  const selectedImages: any[] = [];
  const handleImageSelect = () => {};
  const handleInsertImages = () => {};
  const handleSubmit = () => {};

  const {
    data: DocumnetPreviewData,
    isLoading,
    isError,
  } = DocumentUploadReport(template_id);

  const [pageHtmlChunks, setPageHtmlChunks] = useState<string[]>([]);
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  const [docHeaderHtml, setDocHeaderHtml] = useState<string>("");
  const [docFooterHtml, setDocFooterHtml] = useState<string>("");
  const [activeEditor, setActiveEditor] = useState<any>(null);
  // Tracks every currently-mounted (non-destroyed) page editor so that, if
  // the active one is destroyed (e.g. its page got auto-removed), we can
  // fall back to another live editor instead of hiding the toolbar.
  const liveEditorsRef = useRef<Set<any>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const { authAxios } = useFetchContext();
  const { mutateAsync: UploadManualTipTapSave } =
    UseuploadEditorTipTapManualSave();

  const isContentSet = useRef(false);
  const [lsdbDialogOpen, setLsdbDialogOpen] = useState(false);
  const [lsdbButtonName, setLsdbButtonName] = useState("");

  const handleInsertButton = () => {
    if (!activeEditor) return;
    setLsdbButtonName("");
    setLsdbDialogOpen(true);
  };

  const handleConfirmLsdbButton = () => {
    if (!activeEditor || !lsdbButtonName.trim()) return;

    activeEditor
      .chain()
      .focus()
      .insertContent({
        type: "lsdbButton",
        attrs: {
          name: lsdbButtonName.trim(),
          text: "PULL FROM LSDB",
          url: "",
          prefill: false,
          is_image: false,
          alignment: "right",
        },
      })
      .run();

    setLsdbDialogOpen(false);
    setLsdbButtonName("");
  };

  // Helper function to extract matches synchronously from the editor document
  const getMatches = (doc: any, findText: string) => {
    if (!findText.trim()) return [];

    const searchText = findText.trim();
    const escapedFindText = searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedFindText, "gi");
    const newMatches: { from: number; to: number }[] = [];

    doc.descendants((node: any, pos: number) => {
      if (!node.isText || !node.text) return;

      let match: RegExpExecArray | null;
      while ((match = regex.exec(node.text)) !== null) {
        newMatches.push({
          from: pos + match.index,
          to: pos + match.index + match[0].length,
        });

        if (match[0].length === 0) {
          regex.lastIndex++;
        }
      }
    });

    return newMatches;
  };

  // ... inside your component:

  useEffect(() => {
    if (!activeEditor) return;

    const updateMatches = () => {
      const newMatches = getMatches(activeEditor.state.doc, findText);
      setMatches(newMatches);

      setCurrentMatchIndex((previousIndex) => {
        if (newMatches.length === 0) return 0;
        return previousIndex >= newMatches.length ? 0 : previousIndex;
      });
    };

    updateMatches();

    activeEditor.on("update", updateMatches);
    return () => {
      activeEditor.off("update", updateMatches);
    };
  }, [findText, activeEditor]);

  const handleNextMatch = () => {
    if (matches.length === 0 || !activeEditor) return;

    const nextIdx = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(nextIdx);

    activeEditor
      .chain()
      .focus()
      .setTextSelection({
        from: matches[nextIdx].from,
        to: matches[nextIdx].to,
      })
      .scrollIntoView()
      .run();
  };

  const handlePrevMatch = () => {
    if (matches.length === 0 || !activeEditor) return;

    const prevIdx = (currentMatchIndex - 1 + matches.length) % matches.length;
    setCurrentMatchIndex(prevIdx);

    activeEditor
      .chain()
      .focus()
      .setTextSelection({
        from: matches[prevIdx].from,
        to: matches[prevIdx].to,
      })
      .scrollIntoView()
      .run();
  };

  const handleExecuteFindReplace = (replaceSingle: boolean = false) => {
    if (!findText || matches.length === 0 || !activeEditor) {
      return;
    }

    if (replaceSingle) {
      const currentMatch = matches[currentMatchIndex];
      if (!currentMatch) return;

      // 1. Perform the single replacement
      activeEditor
        .chain()
        .focus()
        .insertContentAt(
          {
            from: currentMatch.from,
            to: currentMatch.to,
          },
          replaceText,
        )
        .run();

      // 2. Immediately scan the updated document synchronously
      const updatedMatches = getMatches(activeEditor.state.doc, findText);
      setMatches(updatedMatches);

      // 3. Highlight and move selection to the next match
      if (updatedMatches.length > 0) {
        const nextIdx =
          currentMatchIndex >= updatedMatches.length ? 0 : currentMatchIndex;

        setCurrentMatchIndex(nextIdx);

        activeEditor
          .chain()
          .focus()
          .setTextSelection({
            from: updatedMatches[nextIdx].from,
            to: updatedMatches[nextIdx].to,
          })
          .scrollIntoView()
          .run();
      } else {
        setCurrentMatchIndex(0);
      }

      return;
    }

    // Replace All (from bottom to top)
    const reversedMatches = [...matches].reverse();
    let chain = activeEditor.chain().focus();

    reversedMatches.forEach((match) => {
      chain = chain.insertContentAt(
        {
          from: match.from,
          to: match.to,
        },
        replaceText,
      );
    });

    chain.run();
  };

  const defaultChromeConfig: ChromeConfig = useMemo(() => {
    let rawData = DocumnetPreviewData;
    if (typeof rawData === "string") {
      try {
        rawData = JSON.parse(rawData);
      } catch (e) {}
    }
    if (rawData?.data) rawData = rawData.data;

    const elements =
      rawData?.placeholder_schema?.content || rawData?.content || [];
    const configElement =
      elements.find((el: any) => el.type === "editor_props") ||
      elements.find((el: any) => el.type === "chrome");
    const jsonConfig = configElement?.attrs?.data || configElement?.data;

    return {
      header: {
        label: jsonConfig?.header?.label || "Preview Report",
        src:
          jsonConfig?.header?.logoDataUri ||
          jsonConfig?.header_default?.images?.[0]?.data_base64 ||
          undefined,
        separatorColor: jsonConfig?.header?.separatorColor || "#095ae8",
      },
      footer: {
        left: jsonConfig?.footer?.left || "© PVEL LLC",
        center: jsonConfig?.footer?.center || "",
        separatorColor: jsonConfig?.footer?.separatorColor || "#095ae8",
        showPageNumbers: jsonConfig?.footer?.showPageNumbers ?? true,
      },
      pageSetup: {
        widthInches:
          jsonConfig?.pageSetup?.widthInches ||
          jsonConfig?.page_setup?.width_inches ||
          8.27,
        heightInches:
          jsonConfig?.pageSetup?.heightInches ||
          jsonConfig?.page_setup?.height_inches ||
          11.69,
        marginTop:
          jsonConfig?.pageSetup?.marginTop ||
          jsonConfig?.page_setup?.margin_top ||
          0.8,
        marginBottom:
          jsonConfig?.pageSetup?.marginBottom ||
          jsonConfig?.page_setup?.margin_bottom ||
          0.8,
        marginLeft:
          jsonConfig?.pageSetup?.marginLeft ||
          jsonConfig?.page_setup?.margin_left ||
          0.8,
        marginRight:
          jsonConfig?.pageSetup?.marginRight ||
          jsonConfig?.page_setup?.margin_right ||
          0.8,
      },
      differentFirstPage: jsonConfig?.differentFirstPage ?? false,
    };
  }, [DocumnetPreviewData]);

  const contentHeightPx = useMemo(() => {
    const { heightInches } = defaultChromeConfig.pageSetup;
    const pageHeightPx = heightInches * 96;
    // NOTE: marginTop/marginBottom are not rendered as real vertical spacing
    // anywhere in this page chrome (content area only ever uses a fixed
    // vertical padding), so subtracting them here reserved space that was
    // never actually consumed. Combined with the fixed page minHeight and
    // the footer being pinned to the bottom, that unclaimed space showed up
    // as a large blank gap near the bottom of every page. See TipTap.tsx's
    // contentHeightPx for the same fix applied there.
    const HEADER_HEIGHT_PX = docHeaderHtml ? 85 : 50;
    const FOOTER_HEIGHT_PX = docFooterHtml ? 55 : 40;
    const CONTENT_VERTICAL_PADDING = 40;
    const availableHeight =
      pageHeightPx -
      HEADER_HEIGHT_PX -
      FOOTER_HEIGHT_PX -
      CONTENT_VERTICAL_PADDING;
    return Math.max(availableHeight, 400);
  }, [defaultChromeConfig, docHeaderHtml, docFooterHtml]);

  // The cover/first page doesn't render the header/footer chrome at all
  // (showChrome is always false for isFirstPage), so it genuinely has more
  // usable room than contentHeightPx assumes (which reserves header/footer
  // space for every page uniformly). Without this, the cover page's own
  // trailing content (e.g. a logo right after the title block) gets
  // bumped to page 2 even though there's clearly unused space left on
  // page 1. Mirrors the same fix in TipTap.tsx.
  const firstPageContentHeightPx = useMemo(() => {
    const { heightInches } = defaultChromeConfig.pageSetup;
    const pageHeightPx = heightInches * 96;
    const CONTENT_VERTICAL_PADDING = 40;
    return Math.max(pageHeightPx - CONTENT_VERTICAL_PADDING, 400);
  }, [defaultChromeConfig]);

  const sanitizeTableDimensions = useCallback((rawHtml: string): string => {
    if (!rawHtml) return "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, "text/html");

    doc.querySelectorAll("col").forEach((col) => {
      col.removeAttribute("style");
      col.removeAttribute("width");
    });

    doc.querySelectorAll("table").forEach((table) => {
      const htmlTable = table as HTMLTableElement;
      const hasResizedColumns = htmlTable.querySelector(
        "td[colwidth], th[colwidth]",
      );
      if (!hasResizedColumns) {
        htmlTable.style.tableLayout = "auto";
      } else {
        htmlTable.style.tableLayout = "fixed";
      }
    });

    doc.querySelectorAll("tr").forEach((row) => {
      const htmlRow = row as HTMLElement;
      htmlRow.removeAttribute("height");
      htmlRow.style.height = "";
    });

    doc.querySelectorAll("td, th").forEach((cell) => {
      const htmlCell = cell as HTMLElement;
      htmlCell.removeAttribute("height");
      if (htmlCell.getAttribute("rowspan") === "2") {
        htmlCell.setAttribute("rowspan", "1");
      }
      htmlCell.style.height = "";
    });

    doc.querySelectorAll("img").forEach((img, index) => {
      const htmlImg = img as HTMLElement;
      htmlImg.style.maxWidth = "100%";
      if (index === 0) {
        htmlImg.style.maxHeight = "400px";
      } else {
        htmlImg.style.maxHeight = "750px";
      }
      htmlImg.style.objectFit = "cover";
    });

    let cleaned = doc.body.innerHTML;
    return cleaned.replace(/<\/table>\s*<table/gi, "</table><p></p><table");
  }, []);

  useEffect(() => {
    if (isContentSet.current) return;
    if (!DocumnetPreviewData) return;

    let rawData = DocumnetPreviewData;
    if (typeof rawData === "string") {
      try {
        rawData = JSON.parse(rawData);
      } catch (e) {}
    }
    if (rawData?.data) rawData = rawData.data;

    let contentToInject: any = { type: "doc", content: [] };
    if (rawData?.placeholder_schema?.content) {
      contentToInject.content = rawData.placeholder_schema.content;
    } else if (rawData?.content) {
      contentToInject.content = rawData.content;
    }

    contentToInject = sanitizeTipTapJSON(contentToInject);
    let bodyNodes = [...(contentToInject.content || [])];

    const headerNode = bodyNodes.find((n: any) => n.type === "header");
    let currentHeaderHtml = "";
    if (headerNode) {
      currentHeaderHtml = generateHTML(
        { type: "doc", content: [headerNode] },
        EDITOR_EXTENSIONS,
      );
    }

    const footerNode = bodyNodes.find((n: any) => n.type === "footer");
    let currentFooterHtml = "";
    if (footerNode) {
      currentFooterHtml = generateHTML(
        { type: "doc", content: [footerNode] },
        EDITOR_EXTENSIONS,
      );
    }

    bodyNodes = bodyNodes.filter(
      (n: any) =>
        n.type !== "chrome" &&
        n.type !== "editor_props" &&
        n.type !== "header" &&
        n.type !== "footer",
    );

    setDocHeaderHtml(currentHeaderHtml);
    setDocFooterHtml(currentFooterHtml);

    const rawBodyHtml = generateHTML(
      { type: "doc", content: bodyNodes },
      EDITOR_EXTENSIONS,
    );
    const cleanBodyHtml = sanitizeTableDimensions(rawBodyHtml);
    const nodes = splitTopLevelNodes(cleanBodyHtml);

    while (nodes.length > 0 && isEffectivelyEmpty(nodes[0])) {
      nodes.shift();
    }

    // Pack the body's top-level nodes onto pages using the same engine
    // TipTap.tsx uses: keeps a table (or an <lsdb-button> + table cluster)
    // intact rather than split by row, and keeps a document's own "Table
    // of Contents" block intact while always forcing a page break right
    // after it, so no other section (e.g. a "Summary") ever shares the
    // TOC's page. The first page gets its own (larger) budget since its
    // header/footer chrome is hidden.
    const chunks: string[] = packNodesIntoPages(
      nodes,
      contentHeightPx,
      640,
      firstPageContentHeightPx,
    );

    let validChunks = chunks.filter((chunk) => !isEffectivelyEmpty(chunk));
    if (validChunks.length > 1 && isEffectivelyEmpty(validChunks[0])) {
      validChunks.shift();
    }
    if (validChunks.length === 0) validChunks = ["<p></p>"];

    setPageHtmlChunks(validChunks);
    isContentSet.current = true;
    setIsDocumentLoading(false);
  }, [
    DocumnetPreviewData,
    contentHeightPx,
    firstPageContentHeightPx,
    sanitizeTableDimensions,
  ]);

  const handlePageChunkChange = (index: number, newHtml: string) => {
    setPageHtmlChunks((prev) => {
      const updated = [...prev];
      updated[index] = newHtml;

      // Auto-remove a page the user has emptied out while editing (e.g.
      // selected-all + deleted its content), unless it's the only page
      // left — the document always needs at least one editable page.
      if (updated.length > 1 && isEffectivelyEmpty(newHtml)) {
        updated.splice(index, 1);
      }

      return updated;
    });
  };

  // Recompute the section navigation list whenever the paginated content
  // changes, by parsing each page's HTML for heading tags.
  useEffect(() => {
    const parser = new DOMParser();
    const allHeadings: typeof navHeadings = [];

    pageHtmlChunks.forEach((html, pageIndex) => {
      const parsedDoc = parser.parseFromString(html || "", "text/html");
      const headingEls = Array.from(parsedDoc.querySelectorAll("h1, h2, h3"));
      headingEls.forEach((el, indexWithinPage) => {
        const text = el.textContent?.trim();
        if (!text) return;
        allHeadings.push({
          pageIndex,
          indexWithinPage,
          level: Number(el.tagName.substring(1)),
          text,
        });
      });
    });

    setNavHeadings(allHeadings);
  }, [pageHtmlChunks]);

  const handlePageOverflow = useCallback(
    (pageIndex: number, overflowHtml: string) => {
      if (!overflowHtml) return;
      setPageHtmlChunks((prev) => {
        const updated = [...prev];
        if (pageIndex === updated.length - 1) updated.push(overflowHtml);
        else updated[pageIndex + 1] = overflowHtml + updated[pageIndex + 1];
        return updated;
      });
      setIsDirty(true);
    },
    [],
  );

  const handleEditorFocus = useCallback((editorInstance: any) => {
    liveEditorsRef.current.add(editorInstance);
    setActiveEditor((prev: any) => {
      if (prev === editorInstance) return prev;
      return editorInstance;
    });
  }, []);

  // A page can be removed (e.g. the user empties it out) while its editor
  // is the active one. Without this, `activeEditor` keeps pointing at a
  // destroyed TipTap instance and the toolbar crashes calling editor.can().
  // Fall back to another live editor (instead of null) so the toolbar
  // itself never disappears.
  const handleEditorDestroy = useCallback((editorInstance: any) => {
    liveEditorsRef.current.delete(editorInstance);
    setActiveEditor((prev: any) => {
      if (prev !== editorInstance) return prev;
      const fallback = liveEditorsRef.current.values().next().value;
      return fallback ?? null;
    });
  }, []);

  const handleUserEdit = useCallback(() => {
    setIsDirty(true);
  }, []);

  const getFullDocumentTipTapJSON = useCallback(() => {
    const fullBodyHtml = pageHtmlChunks.join("");
    const fullHtml = `${docHeaderHtml}${fullBodyHtml}${docFooterHtml}`;
    return generateJSON(fullHtml, EDITOR_EXTENSIONS);
  }, [pageHtmlChunks, docHeaderHtml, docFooterHtml]);

  const fullTipTapJSON = useMemo(
    () => getFullDocumentTipTapJSON(),
    [getFullDocumentTipTapJSON],
  );
  const tocItems = useMemo(
    () => extractTOCItems(fullTipTapJSON),
    [fullTipTapJSON],
  );

  // Handles a click on an entry extracted from the document's own Table of
  // Contents. Works for any document type since it relies solely on the
  // heading/TOC text mapping produced by extractTOCItems, not on hardcoded
  // section names.
  const handleTocItemClick = useCallback(
    (item: TOCItem) => {
      const key = normalizeTitle(item.title);
      const occurrence = tocItems
        .slice(0, item.itemIndex)
        .filter((i) => normalizeTitle(i.title) === key).length;

      const found =
        (item.headingId && scrollToHeading(item.headingId)) ||
        scrollToHeadingByTitle(item.title, occurrence);

      if (!found) {
        console.warn(`Could not find heading for: "${item.title}"`);
      }
      setShowNavPane(false);
    },
    [tocItems],
  );

  const handlePrint = () => {
    const editorCanvas = document.querySelector(".upload-editor-canvas");

    if (!editorCanvas) {
      toast.error("Could not find document to print.");
      return;
    }

    const clone = editorCanvas.cloneNode(true) as HTMLElement;
    clone
      .querySelectorAll("lsdb-button, .lsdb-button-node")
      .forEach((el) => el.remove());

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow pop-ups to print.");
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Document</title>
          <style>
            @page { size: A4 portrait; margin: 0; }
            body {
              margin: 0;
              font-family: "Aptos", Calibri, Arial, sans-serif;
              font-size: 11pt;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .upload-editor-canvas > div { transform: none !important; }
            .docgen-page {
              box-shadow: none !important;
              border: none !important;
              margin: 0 auto !important;
              page-break-after: always;
            }
            .docgen-page:last-of-type {
              page-break-after: auto;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 12px 0;
              table-layout: fixed;
            }
            th, td {
              border: 1px solid black;
              padding: 4px 8px;
              word-break: break-word;
              font-size: 10pt;
            }
            th {
              text-align: center;
              font-weight: bold;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            img {
              max-width: 100%;
              display: block;
              margin: 0 auto;
            }
            .docgen-page:first-of-type table {
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          <div class="upload-editor-canvas">
            ${clone.innerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  useEffect(() => {
    if (!activeEditor) {
      setCurrentPageNumber(1);
      return;
    }

    const pageEditors = Array.from(
      document.querySelectorAll(".docgen-page-content .ProseMirror"),
    ) as HTMLElement[];

    const currentPageIndex = pageEditors.findIndex(
      (pageElement) => pageElement === activeEditor.view?.dom,
    );

    if (currentPageIndex >= 0) {
      setCurrentPageNumber(currentPageIndex + 1);
    }
  }, [activeEditor, pageHtmlChunks.length]);

  const handleDeleteCurrentPage = useCallback(() => {
    if (!activeEditor) {
      toast.info("Please place the cursor on a page first.");
      return;
    }

    const pageEditors = Array.from(
      document.querySelectorAll(".docgen-page-content .ProseMirror"),
    ) as HTMLElement[];

    const currentPageIndex = pageEditors.findIndex(
      (el) => el === activeEditor.view?.dom,
    );

    if (currentPageIndex === -1) {
      toast.error("Unable to determine the current page.");
      return;
    }

    if (pageHtmlChunks.length <= 1) {
      toast.info("The document must contain at least one page.");
      return;
    }

    if (currentPageIndex === 0) {
      toast.info("The cover page cannot be deleted.");
      return;
    }

    setDeletePageIndex(currentPageIndex);
    setDeletePageNumber(currentPageIndex + 1);
    setShowDeletePageDialog(true);
  }, [activeEditor, pageHtmlChunks.length]);

  const confirmDeleteCurrentPage = useCallback(() => {
    if (deletePageIndex === null) return;

    const indexToDelete = deletePageIndex;
    const pageNumber = indexToDelete + 1;

    setPageHtmlChunks((prev) => {
      if (prev.length <= 1) return prev;
      const updated = [...prev];
      updated.splice(indexToDelete, 1);
      return updated;
    });

    setIsDirty(true);
    setActiveEditor(null);
    setShowDeletePageDialog(false);
    setDeletePageIndex(null);
    setDeletePageNumber(null);

    // Re-activate an editor on the page that now sits at this position
    setTimeout(() => {
      const els = document.querySelectorAll(
        ".docgen-page-content .ProseMirror",
      );
      const target = els[Math.min(indexToDelete, els.length - 1)] as any;
      if (target?.editor) setActiveEditor(target.editor);
    }, 100);

    toast.success(
      `Page ${pageNumber} deleted. Click Save to keep this change.`,
    );
  }, [deletePageIndex]);

  const handleSave = async () => {
    if (!pageHtmlChunks || pageHtmlChunks.length === 0) return;
    const fullTipTapJSON = getFullDocumentTipTapJSON();

    try {
      await UploadManualTipTapSave({
        authAxios,
        report_type_id: DocumnetPreviewData?.reporttype_id,
        version: DocumnetPreviewData?.version,
        placeholder_schema: fullTipTapJSON,
      });
      setIsDirty(false);
      toast.success("Document saved successfully!");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          "Failed to save document. Please try again.",
      );
    }
  };

  if (isLoading || isDocumentLoading)
    return (
      <div style={{ padding: 40 }}>
        Loading document... <LinearProgress />
      </div>
    );
  if (isError)
    return <div style={{ padding: 40 }}>Error loading document.</div>;

  const toggleTheme = () => setDarkMode((prev) => !prev);

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: darkMode ? "#18181b" : "#ffffff",
          height: "100vh",
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <style>{`
          @font-face {
            font-family: "Aptos";
            src: url("/Fonts/Aptos.ttf") format("truetype");
            font-weight: 400;
            font-style: normal;
          }

          @font-face {
            font-family: "Aptos";
            src: url("/Fonts/Aptos-Bold.ttf") format("truetype");
            font-weight: 700;
            font-style: normal;
          }

          @font-face {
            font-family: "Aptos";
            src: url("/Fonts/Aptos-Italic.ttf") format("truetype");
            font-weight: 400;
            font-style: italic;
          }

          @font-face {
            font-family: "Aptos";
            src: url("/Fonts/Aptos-Bold-Italic.ttf") format("truetype");
            font-weight: 700;
            font-style: italic;
          }

          html, body {
            margin: 0;
            height: 100%;
            overflow: hidden;
          }

          @media print {
            @page { size: A4 portrait; margin: 0; }
            body { background: #fff !important; }
            .upload-editor-canvas {
              padding: 0 !important;
              background: #fff !important;
              overflow: visible !important;
            }
            .docgen-page { 
              box-shadow: none !important; 
              border: none !important; 
              margin: 0 auto !important; 
              page-break-after: always; 
            }
            .docgen-page:last-of-type {
              page-break-after: auto;
            }
          }

          .upload-editor-canvas {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            overflow-x: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 30px 20px 60px;
            background: #f5f4f0;
            box-sizing: border-box;
          }

          .ProseMirror {
            outline: none;
            color: #000;
            font-family: "Aptos", Arial, sans-serif !important;
            font-size: 11pt;
            line-height: 1;
            word-wrap: break-word;
            min-height: 200px;
          }
          .ProseMirror a[data-action="open-image-drawer"] {
            cursor: pointer !important;
            color: #1976D2 !important;
            text-decoration: underline !important;
          }
          .ProseMirror p { margin: 0 0 8px; }

          .ProseMirror th[data-text-direction = 'True']{
            writing-mode: vertical-rl;
            transform: rotate(180deg);
            white-space: normal;
            vertical-align: middle !important;
            text-align: center !important;
          }

          .upload-editor-canvas .ProseMirror table {
            width: 100%;
            border-collapse: collapse !important;
            margin: 8px 0 !important;
            table-layout: auto !important;
          }

          .upload-editor-canvas .ProseMirror tr {
            height: auto !important;
          }
          .upload-editor-canvas .ProseMirror th,
          .upload-editor-canvas .ProseMirror td {
            border: 1px solid #000 !important;
            padding: 2px 4px !important;
            vertical-align: middle !important;
            word-break: normal !important;
            white-space: normal !important;
            font-size: 8pt !important;
            line-height: 1.05 !important;
            height: auto !important;
            position: relative !important;
            font-weight:normal;
          }
          
          .upload-editor-canvas .ProseMirror td:first-child,
          .upload-editor-canvas .ProseMirror th:first-child {
            text-align: left !important;
            font-weight: normal;
          }

          .ProseMirror .column-resize-handle {
            position: absolute;
            right: -2px;
            top: 0;
            bottom: -2px;
            width: 4px;
            background-color: #095ae8;
            pointer-events: none;
          }

          .upload-editor-canvas .ProseMirror table .selectedCell {
            position: relative;
            box-shadow: inset 0 0 0 9999px rgba(106, 164, 223, 0.25);
          }

          .upload-editor-canvas .ProseMirror table .selectedCell::after {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            width: 100%;
            height: 100%;
            background: rgba(25, 118, 210, 0.2);
            pointer-events: none;
          }

          .docgen-page-content .ProseMirror {
            min-height: 100%;
            cursor: text;
          }

          .upload-editor-canvas .ProseMirror td:first-child:not([colwidth]),
          .upload-editor-canvas .ProseMirror th:first-child:not([colwidth]) {
            width: 35%;
            text-align: left;
            font-weight: normal;          
            }

          .docgen-page-header .ProseMirror table,
          .docgen-page-footer .ProseMirror table {
            width: 100% !important;
            border-collapse: collapse !important;
            margin: 0 !important;
          }
          .docgen-page-header .ProseMirror th,
          .docgen-page-header .ProseMirror td,
          .docgen-page-footer .ProseMirror th,
          .docgen-page-footer .ProseMirror td {
            border: none !important;
            padding: 0 4px !important;
            vertical-align: bottom !important;
            font-size: 9pt !important;
            color: #777 !important;
          }
          .docgen-page-header .ProseMirror p,
          .docgen-page-footer .ProseMirror p {
            margin: 0 !important;
          }

          .upload-editor-canvas .ProseMirror img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
            cursor: pointer;
          }

          .ProseMirror img.ProseMirror-selectednode {
            outline: 3px solid #095ae8 !important;
          }

          .ProseMirror img[width] {
            width: attr(width, length, 50%);
            max-width: 100%;
          }

          .ProseMirror td:has(img) {
            padding: 5px !important;
            line-height: 10px !important;
          }
          .ProseMirror td p,
          .ProseMirror th p {
            margin: 0 !important;
            line-height: 1.6 !important;
          }
          .ProseMirror td:has(img) p {
            margin: 0 !important;
            line-height: 0px !important;
          }

          .ProseMirror lsdb-button button,
          .ProseMirror .lsdb-button-node button {
            padding: 4px 4px !important;
            font-size: 6pt !important;
            min-height: 18px !important;
            height: auto !important;
          }
          .ProseMirror lsdb-button svg,
          .ProseMirror .lsdb-button-node svg {
            width: 10px !important;
            height: 10px !important;
          }

          .ProseMirror div[data-chrome],
          .ProseMirror div[data-editor-props] {
            display: none;
          }

          .docgen-page:first-of-type .docgen-page-content {
            display: block !important;
            padding: 20px 40px !important;
            margin: 0 !important;
            height: 100% !important;
            box-sizing: border-box !important;
          }

          .docgen-page:first-of-type .ProseMirror {
            width: 100% !important;
            min-height: 100% !important;
            outline: none !important;
            padding: 0 !important;
            box-sizing: border-box;
            cursor: text !important;
          }

          .docgen-page:first-of-type .ProseMirror > p {
            margin: 0 0 8px !important;
            cursor: text !important;
            color: #0d2c54 !important;
            font-size: 11pt !important;
          }

          /* COVER HERO IMAGE: when the very first element on the cover
             page is an image, let it bleed to the true page edges
             (ignoring the cover page's 20px/40px content padding) while
             every other element on the page keeps its own padding/margin
             untouched. This intentionally does NOT force any
             width/height/rotation on the image anymore, so resizing and
             rotating the cover image works exactly like on any other
             page. Targets the paragraph wrapping the image node view
             (the actual <img> sits a few levels deep inside it, since the
             image is rendered via an inline NodeView, not a bare <img>). */
          .docgen-page:first-of-type .ProseMirror > :first-child:has(img) {
            margin: -20px -40px 16px -40px !important;
            width: calc(100% + 80px) !important;
          }
          /* Zero out the image node's own inline alignment margin (set by
             ImageNodeView's NodeViewWrapper, e.g. "8px auto 8px 0") so no
             residual gap remains between the page edge and the bled image
             once the paragraph above is flush with it. */
          .docgen-page:first-of-type .ProseMirror > :first-child:has(img) > [data-alignment] {
            margin: 0 !important;
          }

          .docgen-page:first-of-type .ProseMirror > table,
          .docgen-page:first-of-type .ProseMirror table {
            width: 100% !important;
            background-color: #ffffff !important;
            border-collapse: collapse !important;
            margin-top: 20px !important;
          }
          
          .docgen-page:first-of-type .ProseMirror td p,
          .docgen-page:first-of-type .ProseMirror th p {
            margin: 0 !important;
            padding: 0 !important;
            color: #0d2c54 !important;
            font-size: 9pt !important;
            line-height: 1.2 !important;
          }
          
          .docgen-page:first-of-type .ProseMirror td,
          .docgen-page:first-of-type .ProseMirror th {
            border: 1px solid #0d2c54 !important;
            padding: 4px 6px !important;
            font-size: 9pt !important;
            color: #0d2c54 !important;
            background-color: #ffffff !important;
            vertical-align: middle !important;
          }

          .docgen-page .ProseMirror table {
            page-break-inside: avoid;
          }
              /* --- Column resize handle (the vertical drag line between columns) --- */
.ProseMirror .tableWrapper {
  overflow-x: auto;
}

.ProseMirror table {
  position: relative;
}

.ProseMirror td,
.ProseMirror th {
  position: relative;
}

.ProseMirror .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: -2px;
  width: 2px;
  z-index: 20;
  background-color: #1976D2;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.1s ease;
}

/* Show it while the mouse is actually over that column border */
.ProseMirror td:hover > .column-resize-handle,
.ProseMirror th:hover > .column-resize-handle,
.ProseMirror .column-resize-handle {
  opacity: 1;
}

/* Cursor + handle color while actively dragging */
.ProseMirror.resize-cursor {
  cursor: col-resize;
}
.ProseMirror.resize-cursor .column-resize-handle {
  background-color: #101212;
  opacity: 0;
}
        `}</style>

        {/* =========================
            TITLE BAR & TOOLBAR CONTAINER
            ========================= */}
        <div
          style={{
            flexShrink: 0,
            width: "100%",
            maxWidth: "100%",
            background: "#fff",
            zIndex: 50,
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <TipTapTitleBar
            title={reportNumber}
            version={reportVersion}
            versionHistory={version_history}
            status={reportData?.status}
            ProjectId={ProjectId}
            onBack={() => window.history.back()}
            workOrder={workOrder}
            onSubmit={handleSubmit}
            reportImages={reportImages}
            selectedImages={selectedImages}
            onImageSelect={handleImageSelect}
            onInsertImages={handleInsertImages}
          />

          {activeEditor && (
            <TipTapToolbar
              editor={activeEditor}
              onSave={handleSave}
              onPrint={handlePrint}
              zoom={100}
              setZoom={() => {}}
              darkMode={darkMode}
              toggleTheme={toggleTheme}
              workorder={0}
              onInsertButton={handleInsertButton}
              onOpenFindReplace={() => setShowFindReplace(true)}
              onOpenNavPane={() => setShowNavPane(true)}
              onDeletePage={handleDeleteCurrentPage}
              onConfirmDeletePage={confirmDeleteCurrentPage}
              currentPageNumber={currentPageNumber}
            />
          )}

          {showFindReplace && (
            <FindReplaceWidget
              onClose={() => setShowFindReplace(false)}
              findText={findText}
              setFindText={setFindText}
              replaceText={replaceText}
              setReplaceText={setReplaceText}
              onExecuteReplace={handleExecuteFindReplace}
              matchCount={matches.length}
              currentIndex={currentMatchIndex}
              onNext={handleNextMatch}
              onPrev={handlePrevMatch}
            />
          )}
        </div>

        {/* =========================
            SCROLLABLE EDITOR CANVAS
            ========================= */}
        <div
          className="upload-editor-canvas"
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            minHeight: 0,
            width: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            background: darkMode ? "#27272a" : "#f5f4f0",
            boxSizing: "border-box",
            padding: "30px 20px 60px",
          }}
        >
          <div
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: "top center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            {isDocumentLoading ? (
              <div
                style={{
                  padding: "40px",
                  color: "#888",
                  fontStyle: "italic",
                  fontFamily: "Arial",
                }}
              >
                Loading document...
              </div>
            ) : (
              pageHtmlChunks.map((htmlChunk, pageIndex) => (
                <PageChrome
                  key={pageIndex}
                  chrome={defaultChromeConfig}
                  contentHeightPx={contentHeightPx}
                  pageNumber={pageIndex + 1}
                  totalPages={pageHtmlChunks.length}
                  isFirstPage={pageIndex === 0}
                  customHeaderHtml={docHeaderHtml}
                  customFooterHtml={docFooterHtml}
                  activeEditor={activeEditor}
                >
                  <SinglePageEditor
                    contentHtml={htmlChunk}
                    maxHeightPx={
                      pageIndex === 0
                        ? firstPageContentHeightPx
                        : contentHeightPx
                    }
                    onChange={(newHtml) =>
                      handlePageChunkChange(pageIndex, newHtml)
                    }
                    onFocus={handleEditorFocus}
                    onUserEdit={handleUserEdit}
                    onOverflow={(html) => handlePageOverflow(pageIndex, html)}
                    onEditorDestroy={handleEditorDestroy}
                  />
                </PageChrome>
              ))
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={lsdbDialogOpen}
        onClose={() => setLsdbDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter LSDB Data Fetch Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label="Data Fetch Name"
            placeholder="e.g. Report Details"
            value={lsdbButtonName}
            onChange={(e) => setLsdbButtonName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConfirmLsdbButton();
              }
            }}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLsdbDialogOpen(false);
              setLsdbButtonName("");
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLsdbButton}
            color="primary"
            variant="contained"
            disabled={!lsdbButtonName.trim()}
          >
            Insert Button
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showDeletePageDialog}
        onClose={() => setShowDeletePageDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>page {deletePageNumber}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeletePageDialog(false);
              setDeletePageIndex(null);
              setDeletePageNumber(null);
            }}
            color="default"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteCurrentPage}
            color="secondary"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="left"
        open={showNavPane}
        onClose={() => setShowNavPane(false)}
      >
        <Box width={300} role="presentation" style={{ padding: 16 }}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 600, marginBottom: 8 }}
          >
            {tocItems.length > 0 ? "Table of Contents" : "Report Sections"}
          </Typography>
          <Divider />
          {tocItems.length > 0 ? (
            <TableOfContents
              items={tocItems}
              title={null}
              onNavigate={() => setShowNavPane(false)}
            />
          ) : navHeadings.length === 0 ? (
            <Typography
              variant="body2"
              style={{ color: "#888", padding: "16px 0" }}
            >
              No headings found. Use Heading 1/2/3 styles to build a navigable
              outline.
            </Typography>
          ) : (
            <List dense>
              {navHeadings.map((heading, idx) => (
                <ListItem
                  key={idx}
                  button
                  onClick={() => handleNavHeadingClick(heading)}
                  style={{ paddingLeft: 8 + (heading.level - 1) * 16 }}
                >
                  <ListItemText
                    primary={heading.text}
                    primaryTypographyProps={{
                      style: {
                        fontSize: heading.level === 1 ? 14 : 13,
                        fontWeight: heading.level === 1 ? 600 : 400,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
    </>
  );
};
